import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { setUserProfile } from '../store/userSlice';
import { GET_PROFILE } from './Queries';

// Profile context for managing profile updates across the app
const ProfileContext = createContext({
  refetchProfile: async () => {},
  profileLoading: false,
  profileData: null,
});

export const ProfileProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);

  const { data: profileData, loading: profileLoading, refetch: refetchProfile } = useQuery(
    GET_PROFILE,
    {
      // Skip when the user isn't logged in to avoid 401 noise.
      skip: !isAuthenticated,
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        if (data?.getProfile?.profile) {
          // Update Redux store with fresh profile data
          dispatch(setUserProfile(data.getProfile.profile));
        }
      },
      onError: (error) => {
        console.error('Profile fetch error:', error);
      },
      pollInterval: 0,
      notifyOnNetworkStatusChange: true,
    }
  );

  const handleRefetchProfile = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const result = await refetchProfile();
      if (result?.data?.getProfile?.profile) {
        dispatch(setUserProfile(result.data.getProfile.profile));
      }
      return result;
    } catch (error) {
      console.error('Error refetching profile:', error);
      return null;
    }
  }, [refetchProfile, dispatch, isAuthenticated]);

  return (
    <ProfileContext.Provider
      value={{
        refetchProfile: handleRefetchProfile,
        profileLoading,
        profileData,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export default ProfileContext;
